import styled from "styled-components";

const Container = styled.div`
  width: 80%;
  height: 40px;
  display: ${({ theme }) => (theme.displayProcessState ? "flex" : "none")};
  align-items: center;
  justify-content: center;

  @media (max-width: 480px) {
    width: 95%;
  }
`;

const ProgressNav = styled.nav`
  width: 100%;
`;

const ProgressList = styled.ol`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: start;
  width: 100%;
  padding: 0;
  margin: 0;
  list-style: none;
`;

const ProgressItem = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ItemStack = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

const Line = styled.div<{ $active?: boolean; $side: "left" | "right" }>`
  position: absolute;
  top: 9px;
  height: 2px;
  width: calc(50% - 20px);
  background: ${({ $active }) => ($active ? "#163563" : "#87857A")};
  ${({ $side }) => ($side === "left" ? "left: 0;" : "right: 0;")}
`;

const Circle = styled.div<{
  $state: "done" | "active" | "todo";
}>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 2px solid
    ${({ $state }) => ($state === "todo" ? "#87857A" : "#163563")};
  background: ${({ $state }) =>
    $state === "done"
      ? "#163563"
      : $state === "active"
      ? "#163563"
      : "transparent"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $state }) => ($state === "done" ? "#faf5eb" : "inherit")};
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #faf5eb;
`;

const Label = styled.span<{ $state: "done" | "active" | "todo" }>`
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: ${({ $state }) => ($state === "todo" ? "#55544C" : "#23231F")};
  margin: 0;
`;

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 20 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export interface Authenticating {
  type: "Authenticating";
}
export interface Authenticated {
  type: "Authenticated";
}
export interface Consenting {
  type: "Consenting";
}
export interface Consented {
  type: "Consented";
}

export type ProcessViewState =
  | Authenticating
  | Authenticated
  | Consenting
  | Consented;

export interface ProcessStateImageProps {
  state: ProcessViewState;
}

function mapSteps(state: ProcessViewState): {
  steps: Array<"done" | "active" | "todo">;
  lines: [boolean, boolean];
  labels: [string, string, string];
  testId: string;
} {
  const labels: [string, string, string] = ["Login", "PIN", "Done"];
  switch (state.type) {
    case "Authenticating":
      return {
        steps: ["active", "todo", "todo"],
        lines: [false, false],
        labels,
        testId: "process_state_image_active",
      };
    case "Authenticated":
      return {
        steps: ["active", "todo", "todo"],
        lines: [true, false],
        labels,
        testId: "process_state_image_active",
      };
    case "Consenting":
      return {
        steps: ["done", "active", "todo"],
        lines: [true, false],
        labels,
        testId: "process_state_image_active",
      };
    case "Consented":
      return {
        steps: ["done", "done", "done"],
        lines: [true, true],
        labels,
        testId: "process_state_image_done",
      };
  }
}

export default function ProcessStateImage(
  props: ProcessStateImageProps,
): JSX.Element {
  const { steps, lines, labels, testId } = mapSteps(props.state);
  return (
    <Container data-testid="process_state_image">
      <ProgressNav aria-label="Progress">
        <ProgressList data-testid={testId}>
          {steps.map((step, index) => {
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;
            return (
              <ProgressItem key={labels[index]}>
                <ItemStack>
                  {!isFirst && (
                    <Line $side="left" $active={!!lines[index - 1]} />
                  )}
                  <Circle $state={step}>
                    {step === "done" ? (
                      <CheckIcon />
                    ) : step === "active" ? (
                      <Dot />
                    ) : null}
                  </Circle>
                  {!isLast && <Line $side="right" $active={!!lines[index]} />}
                  <Label $state={step}>{labels[index]}</Label>
                </ItemStack>
              </ProgressItem>
            );
          })}
        </ProgressList>
      </ProgressNav>
    </Container>
  );
}
